import { useState, useEffect } from 'react';
import { Plus, ChevronsDown, ChevronsUp } from 'lucide-react';
import * as API from '../../api';
import { PageTitle } from '../../../_metronic/layout/core';
import ConfirmModal from '../modals/ConfirmModal';
import PermissionsModal from './PermissionModal';
import { EmptyState } from './EmptyState';
import { LoadingState } from './LoadingState';
import { PermissionCard } from './PermissionCard';

type Props = {
  className?: string;
};

const PermissionsScreen: React.FC<Props> = ({ className }) => {
  const [message, setMessage] = useState<string>('');
  const [permissionModal, showPermissionModal] = useState(false);
  const [confirmModal, showConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [permissions, setPermissions] = useState<any>([]);
  const [groupPermissions, setGroupPermissions] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    showConfirmModal(message !== '');
  }, [message]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [permissionsRes, groupPermissionsRes] = await Promise.all([
        API.getPermissions(),
        API.getGroupPermissions(),
      ]);

      setPermissions(permissionsRes.data);
      const groupsData = groupPermissionsRes.data.map((item: any) => ({
        ...item,
        data: JSON.parse(item.data),
      }));
      setGroupPermissions(groupsData);

      const initialExpandedState = groupsData.reduce((acc: Record<string, boolean>, group: any) => {
        acc[group.id_feature_group] = true;
        return acc;
      }, {});
      setExpandedCards(initialExpandedState);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCard = (id: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const expandAll = () => {
    const allExpanded = groupPermissions.reduce((acc: Record<string, boolean>, group: any) => {
      acc[group.id_feature_group] = true;
      return acc;
    }, {});
    setExpandedCards(allExpanded);
  };

  const collapseAll = () => {
    const allCollapsed = groupPermissions.reduce((acc: Record<string, boolean>, group: any) => {
      acc[group.id_feature_group] = false;
      return acc;
    }, {});
    setExpandedCards(allCollapsed);
  };

  const allExpanded = Object.values(expandedCards).every(v => v);
  const allCollapsed = Object.values(expandedCards).every(v => !v);

  const handleNewGroup = () => {
    setSelectedItem(null);
    showPermissionModal(true);
  };

  const handleEditGroup = (group: any) => {
    setSelectedItem(group);
    showPermissionModal(true);
  };

  const handleDeleteGroup = (group: any) => {
    setSelectedItem(group);
    setMessage(
      'Deseja remover esse grupo de permissões? Caso exista usuários vinculados a ele, eles serão afetados por essa ação.'
    );
  };

  const removeItem = async (item: any) => {
    setIsLoading(true);
    try {
      await API.removeGroupPermissions(item);
      await loadData();
    } catch (error) {
      console.error('Error removing group:', error);
    }
  };

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <PageTitle>Grupo de Permissões</PageTitle>
          <button
            onClick={handleNewGroup}
            className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
          >
            <Plus className="w-5 h-5 mr-2" />
            Criar grupo de permissões
          </button>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : groupPermissions.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{groupPermissions.length}</span> {groupPermissions.length === 1 ? 'grupo' : 'grupos'} de permissões
              </div>
              <div className="flex gap-2">
                <button
                  onClick={expandAll}
                  disabled={allExpanded}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  <ChevronsDown className="w-4 h-4" />
                  Expandir Todos
                </button>
                <button
                  onClick={collapseAll}
                  disabled={allCollapsed}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  <ChevronsUp className="w-4 h-4" />
                  Colapsar Todos
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {groupPermissions.map((group: any) => (
                <PermissionCard
                  key={group.id_feature_group}
                  group={group}
                  permissions={permissions}
                  onEdit={handleEditGroup}
                  onDelete={handleDeleteGroup}
                  isExpanded={expandedCards[group.id_feature_group]}
                  onToggleExpand={() => toggleCard(group.id_feature_group)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {permissionModal && (
        <PermissionsModal
          data={permissions}
          group={selectedItem}
          onClose={() => {
            showPermissionModal(false);
            setSelectedItem(null);
          }}
          onChange={() => {
            loadData();
            showPermissionModal(false);
            setSelectedItem(null);
          }}
        />
      )}

      {confirmModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <ConfirmModal
            title={`Grupo: ${selectedItem?.name}`}
            message={message}
            onClose={(value: boolean) => {
              if (value) removeItem(selectedItem);
              setSelectedItem(null);
              setMessage('');
            }}
          />
        </div>
      )}
    </>
  );
};

export default PermissionsScreen;